/**
 * UserTools — login/logout links for the header tools slot.
 *
 * Registered in the "headerTools" slot, replacing the light theme's
 * Anontools which depends on Plone's @actions endpoint (not available
 * in Nick).
 *
 * Reads the auth token from Redux and checks expiration before deciding
 * whether to show Log in or Log out. An expired JWT in the cookie
 * (common when browser tabs persist across sessions) must not show
 * "Log out" — it should show "Log in" and clear the stale cookie.
 *
 * Login/logout links use the current language prefix so that Volto's
 * multilingual Navigation scopes correctly (DR-2026-055).
 */
import { Link, useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector, shallowEqual } from 'react-redux';
import { toBackendLang } from '@plone/volto/helpers/Utils/Utils';
import Cookies from 'universal-cookie';

/**
 * Check if a JWT token is still valid (not expired).
 * Returns false for null, undefined, malformed, or expired tokens.
 */
function isTokenValid(token) {
  if (!token) return false;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1]));
    if (!payload.exp) return false;
    // exp is in seconds, Date.now() in milliseconds
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

const UserTools = () => {
  const token = useSelector(
    (state) => state.userSession?.token,
    shallowEqual,
  );
  const content = useSelector(
    (state) => state.content.data,
    shallowEqual,
  );
  const location = useLocation();
  const intl = useIntl();
  const isMultilingual = useSelector(
    (state) => state.site?.data?.features?.multilingual,
  );

  // In multilingual mode, prefix login/logout with the current language
  const langPrefix = isMultilingual ? `/${toBackendLang(intl.locale)}` : '';

  const valid = isTokenValid(token);

  // If token exists but is expired, clear the stale cookie
  if (token && !valid) {
    const cookies = new Cookies();
    cookies.remove('auth_token', { path: '/' });
  }

  if (valid) {
    return (
      <a
        aria-label="logout"
        href={`${langPrefix}/logout`}
        onClick={(e) => {
          e.preventDefault();
          const cookies = new Cookies();
          cookies.remove('auth_token', { path: '/' });
          window.location.href = `${langPrefix}/login`;
        }}
      >
        <FormattedMessage id="Log out" defaultMessage="Log out" />
      </a>
    );
  }

  const returnUrl = content?.['@id']
    ? content['@id'].replace(/^https?:\/\/[^/]+/, '')
    : location.pathname;

  return (
    <Link
      aria-label="login"
      to={`${langPrefix}/login${returnUrl ? `?return_url=${encodeURIComponent(returnUrl)}` : ''}`}
    >
      <FormattedMessage id="Log in" defaultMessage="Log in" />
    </Link>
  );
};

export default UserTools;
